/**
 * 技能上传模态框
 *
 * 用于创建新的用户自定义技能，支持：
 * - 技能名称输入（hyphen-case格式验证）
 * - 技能内容编辑（YAML frontmatter格式）
 * - 内容大小验证（1MB限制）
 * - 文件夹上传支持（10MB限制）
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { skillApi } from '../../services/skillApi';
import {
  isValidSkillName,
  isValidSkillContentSize,
  hasYamlFrontmatter,
  MAX_SKILL_CONTENT_SIZE,
} from '../../types/skill';
import type { Skill } from '../../types/skill';

// 文件夹上传大小限制 (10MB)
const MAX_FOLDER_SIZE = 10 * 1024 * 1024;

// 不支持的代码脚本扩展名
const CODE_EXTENSIONS = [
  '.py', '.js', '.ts', '.jsx', '.tsx', '.sh', '.bash', '.zsh',
  '.rb', '.php', '.java', '.c', '.cpp', '.go', '.rs', '.swift',
];

interface SkillUploadModalProps {
  /** 是否打开 */
  isOpen: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 创建/编辑成功回调 */
  onSuccess: () => void;
  /** 编辑的技能（如果提供则为编辑模式） */
  skill?: Skill | null;
}

const DEFAULT_CONTENT = `---
name: my-skill
description: 技能描述
---

# 技能标题

在这里编写技能内容...
`;

export const SkillUploadModal: React.FC<SkillUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  skill,
}) => {
  const [name, setName] = useState('');
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [folderWarning, setFolderWarning] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'manual' | 'folder'>('manual');
  const folderInputRef = useRef<HTMLInputElement>(null);

  // 是否为编辑模式
  const isEditMode = !!skill;

  // 加载技能内容（编辑模式）
  useEffect(() => {
    if (!isEditMode || !skill) {
      return;
    }

    const fetchSkillDetail = async () => {
      setLoading(true);
      try {
        const skillDetail = await skillApi.getSkill(skill.name);
        setName(skillDetail.name);
        setContent(skillDetail.content);
      } catch (err) {
        setErrors({ submit: '加载技能内容失败' });
        console.error('加载技能内容失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSkillDetail();
  }, [skill, isEditMode]);

  // 重置表单
  const resetForm = useCallback(() => {
    setName('');
    setContent(DEFAULT_CONTENT);
    setErrors({});
    setFolderWarning(null);
    setUploadMode('manual');
  }, []);

  // 检查文件是否为代码脚本
  const isCodeFile = useCallback((filename: string): boolean => {
    const ext = filename.toLowerCase().slice(filename.lastIndexOf('.'));
    return CODE_EXTENSIONS.includes(ext);
  }, []);

  // 处理文件夹上传
  const handleFolderUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setFolderWarning(null);
    setErrors({});

    // 检查总大小
    let totalSize = 0;
    const codeFiles: string[] = [];
    const skillFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      totalSize += file.size;

      // 检查代码文件
      if (isCodeFile(file.name)) {
        codeFiles.push(file.name);
      }

      // 收集 SKILL.md 文件
      if (file.name.toLowerCase() === 'skill.md') {
        skillFiles.push(file);
      }
    }

    // 检查大小限制
    if (totalSize > MAX_FOLDER_SIZE) {
      setErrors({
        folder: `文件夹总大小(${(totalSize / 1024 / 1024).toFixed(2)}MB)超过限制(10MB)`,
      });
      return;
    }

    // 显示代码脚本警告
    if (codeFiles.length > 0) {
      setFolderWarning(
        `发现 ${codeFiles.length} 个代码脚本文件，将不会执行这些脚本：${codeFiles.slice(0, 3).join(', ')}${codeFiles.length > 3 ? '...' : ''}`
      );
    }

    // 如果有 SKILL.md 文件，读取第一个
    if (skillFiles.length > 0) {
      try {
        const content = await skillFiles[0].text();

        // 解析 YAML frontmatter 获取名称
        const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---/);
        if (frontmatterMatch) {
          const frontmatter = frontmatterMatch[1];
          const nameMatch = frontmatter.match(/name:\s*(.+)/);
          if (nameMatch) {
            setName(nameMatch[1].trim());
          }
        }

        setContent(content);
        setUploadMode('folder');
      } catch (err) {
        setErrors({ folder: '读取 SKILL.md 文件失败' });
      }
    } else {
      // 没有 SKILL.md 文件，从文件夹名称生成
      const folderPath = files[0].webkitRelativePath;
      const folderName = folderPath.split('/')[0];
      const skillName = folderName
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      setName(skillName);

      // 读取 README.md 或创建默认内容
      const readmeFile = Array.from(files).find(
        (f) => f.name.toLowerCase() === 'readme.md'
      );

      if (readmeFile) {
        try {
          const readmeContent = await readmeFile.text();
          const newContent = `---
name: ${skillName}
description: 从文件夹 ${folderName} 导入的技能
---

${readmeContent}`;
          setContent(newContent);
        } catch (err) {
          setErrors({ folder: '读取 README.md 文件失败' });
        }
      } else {
        setContent(`---
name: ${skillName}
description: 从文件夹 ${folderName} 导入的技能
---

# ${folderName}

技能内容...
`);
      }
      setUploadMode('folder');
    }

    // 清空 input 以允许再次选择相同文件夹
    e.target.value = '';
  }, [isCodeFile]);

  // 打开文件夹选择
  const handleSelectFolder = useCallback(() => {
    folderInputRef.current?.click();
  }, []);

  // 关闭时重置
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // 验证表单
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // 验证名称
    if (!name.trim()) {
      newErrors.name = '技能名称不能为空';
    } else if (!isValidSkillName(name)) {
      newErrors.name = '技能名称必须使用hyphen-case格式（如my-skill）';
    }

    // 验证内容
    if (!content.trim()) {
      newErrors.content = '技能内容不能为空';
    } else if (!hasYamlFrontmatter(content)) {
      newErrors.content = '技能内容必须包含YAML frontmatter（以---开始）';
    } else if (!isValidSkillContentSize(content)) {
      newErrors.content = `技能内容不能超过${Math.round(MAX_SKILL_CONTENT_SIZE / 1024 / 1024)}MB`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, content]);

  // 提交表单
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (isEditMode && skill) {
        // 编辑模式
        await skillApi.updateSkill(skill.id, { name, content });
      } else {
        // 创建模式
        await skillApi.createSkill({ name, content });
      }
      onSuccess();
      handleClose();
    } catch (err: any) {
      if (err.message?.includes('已存在') && !isEditMode) {
        setErrors({ name: '技能名称已存在' });
      } else {
        setErrors({ submit: err.message || (isEditMode ? '更新技能失败' : '创建技能失败') });
      }
    } finally {
      setLoading(false);
    }
  }, [name, content, isEditMode, skill, validateForm, onSuccess, handleClose]);

  // 按ESC关闭
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题 */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? '编辑技能' : '创建技能'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 p-1"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* 提交错误 */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {errors.submit}
            </div>
          )}

          {/* 文件夹上传错误 */}
          {errors.folder && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {errors.folder}
            </div>
          )}

          {/* 代码脚本警告 */}
          {folderWarning && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{folderWarning}</span>
              </div>
            </div>
          )}

          {/* 上传模式切换 */}
          <div className="mb-4 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setUploadMode('manual');
                setFolderWarning(null);
                setErrors({});
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                uploadMode === 'manual'
                  ? 'bg-ink-800 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              手动输入
            </button>
            <button
              type="button"
              onClick={handleSelectFolder}
              className={`px-4 py-2 rounded-md text-sm font-medium ${
                uploadMode === 'folder'
                  ? 'bg-ink-800 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              从文件夹导入
            </button>
            {/* 隐藏的文件夹选择 input */}
            <input
              ref={folderInputRef}
              type="file"
              // @ts-ignore - webkitdirectory is not in TypeScript types
              webkitdirectory=""
              directory=""
              onChange={handleFolderUpload}
              className="hidden"
            />
          </div>

          {/* 技能名称 */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-1">
              技能名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="my-skill"
              maxLength={100}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-600">
              使用hyphen-case格式，如：novel-creation、plot-planning
            </p>
          </div>

          {/* 技能内容 */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-1">
              技能内容 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 font-mono text-sm min-h-[300px] ${
                errors.content ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="---&#10;name: skill-name&#10;description: 技能描述&#10;---&#10;&#10;# 技能内容"
            />
            {errors.content && (
              <p className="mt-1 text-sm text-red-600">{errors.content}</p>
            )}
            <p className="mt-1 text-xs text-gray-600">
              内容必须包含YAML frontmatter，以---开始
            </p>
          </div>

          {/* 按钮 */}
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-md"
              disabled={loading}
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-ink-800 hover:bg-ink-700 text-white rounded-md disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (isEditMode ? '保存中...' : '创建中...') : (isEditMode ? '保存' : '创建技能')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SkillUploadModal;
